import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline'

export default function WorkoutPhotoReview({ analysis, session: _session, onApply, onCancel }) {
  const { t } = useTranslation()
  const [expandedExercises, setExpandedExercises] = useState({})

  const [editedExercises, setEditedExercises] = useState(() => {
    return analysis.exercises.reduce((acc, ex, idx) => {
      acc[idx] = {
        session_exercise_id: ex.matched_session_exercise_id,
        sets: ex.sets.map(s => ({
          set_number: s.set_number,
          set_type: s.set_type,
          actual_weight: s.weight_kg,
          actual_reps: s.reps
        }))
      }
      return acc
    }, {})
  })

  if (!analysis) return null

  const toggleExpanded = (index) => {
    setExpandedExercises(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  // Confidence color helpers
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.85) return 'text-green-600'
    if (confidence >= 0.60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getConfidenceBg = (confidence) => {
    if (confidence >= 0.85) return 'bg-green-50'
    if (confidence >= 0.60) return 'bg-yellow-50'
    return 'bg-red-50'
  }

  const getDotColor = (confidence) => {
    if (confidence >= 0.85) return 'bg-green-500'
    if (confidence >= 0.60) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  // Update a set value
  const handleSetChange = (exerciseIdx, setIdx, field, value) => {
    setEditedExercises(prev => {
      const updated = { ...prev }
      const numValue = value === '' ? null : parseFloat(value)
      updated[exerciseIdx].sets[setIdx][field] = numValue
      return updated
    })
  }

  // Handle apply - convert to API format
  const handleApply = () => {
    const exercisesToApply = Object.values(editedExercises)
      .filter(ex => ex.session_exercise_id !== null)
      .map(ex => ({
        session_exercise_id: ex.session_exercise_id,
        sets: ex.sets.map(s => ({
          set_number: s.set_number,
          set_type: s.set_type,
          actual_weight: s.actual_weight,
          actual_reps: s.actual_reps
        }))
      }))

    onApply(exercisesToApply)
  }

  const avgConfidence = analysis.overall_confidence || 0

  return (
    <div className="space-y-6">
      {/* Analysis Header */}
      <div className="bg-surface-container rounded-lg border-2 border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {t('workouts.photo.results', 'Workout Photo Results')}
          </h2>
          <div className={`px-3 py-1 rounded-full ${getConfidenceBg(avgConfidence)}`}>
            <span className={`text-sm font-medium ${getConfidenceColor(avgConfidence)}`}>
              {Math.round(avgConfidence * 100)}% {t('workouts.photo.confidence', 'Confidence')}
            </span>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="text-sm text-indigo-600 font-medium">
              {t('workouts.photo.exercisesFound', 'Exercises Found')}
            </div>
            <div className="text-2xl font-bold text-indigo-900">
              {analysis.exercises?.length || 0}
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-blue-600 font-medium">
              {t('workouts.photo.setsExtracted', 'Sets Extracted')}
            </div>
            <div className="text-2xl font-bold text-blue-900">
              {analysis.exercises?.reduce((sum, ex) => sum + ex.sets.length, 0) || 0}
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm text-green-600 font-medium">
              {t('workouts.photo.matched', 'Matched')}
            </div>
            <div className="text-2xl font-bold text-green-900">
              {analysis.exercises?.filter(ex => ex.matched_session_exercise_id).length || 0}
            </div>
          </div>
        </div>

        {/* Warnings */}
        {analysis.warnings && analysis.warnings.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800 mb-1">
                  {t('workouts.photo.pleaseReview', 'Please Review')}
                </h3>
                <ul className="text-sm text-yellow-700 space-y-1">
                  {analysis.warnings.map((warning, idx) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Unmatched exercises warning */}
        {analysis.unmatched_exercises && analysis.unmatched_exercises.length > 0 && (
          <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-orange-800 mb-1">
                  {t('workouts.photo.unmatchedExercises', 'Unmatched Exercises')}
                </h3>
                <p className="text-sm text-orange-700">
                  {t('workouts.photo.unmatchedDesc', 'These exercises were found but could not be matched to your session:')}
                </p>
                <ul className="text-sm text-orange-700 mt-1 list-disc list-inside">
                  {analysis.unmatched_exercises.map((name, idx) => (
                    <li key={idx}>{name}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Extracted Exercises */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">
          {t('workouts.photo.extractedExercises', 'Extracted Exercises')} ({analysis.exercises?.length || 0})
        </h3>

        {analysis.exercises?.map((exercise, exerciseIdx) => {
          const isExpanded = expandedExercises[exerciseIdx]
          const matchConfidence = exercise.match_confidence || 0
          const isMatched = exercise.matched_session_exercise_id !== null

          return (
            <div key={exerciseIdx} className="bg-surface-container border-2 border-gray-200 rounded-lg overflow-hidden">
              {/* Exercise Header */}
              <button
                onClick={() => toggleExpanded(exerciseIdx)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-4 flex-1">
                  <div className="flex-shrink-0">
                    <div className={`w-2 h-2 rounded-full ${getDotColor(matchConfidence)}`} />
                  </div>
                  <div className="text-left flex-1">
                    <h4 className="text-base font-semibold text-gray-900">
                      {exercise.exercise_name}
                    </h4>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                      <span>{exercise.sets.length} {t('workouts.photo.sets', 'sets')}</span>
                      <span></span>
                      <span className={getConfidenceColor(matchConfidence)}>
                        {Math.round(matchConfidence * 100)}% {t('workouts.photo.match', 'match')}
                      </span>
                      {!isMatched && (
                        <span className="text-orange-600 text-xs">
                          ({t('workouts.photo.notMatched', 'Not matched')})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-400 ml-4" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400 ml-4" />
                )}
              </button>

              {/* Expanded: Editable set inputs */}
              {isExpanded && (
                <div className="px-6 pb-4 border-t border-gray-200">
                  {/* Set Header */}
                  <div className="grid grid-cols-5 gap-4 mt-4 mb-2 text-xs font-medium text-gray-500 uppercase">
                    <span>{t('workouts.photo.setNum', 'Set')}</span>
                    <span>{t('workouts.photo.type', 'Type')}</span>
                    <span>{t('workouts.photo.weight', 'Weight (kg)')}</span>
                    <span>{t('workouts.photo.reps', 'Reps')}</span>
                    <span>{t('workouts.photo.confidence', 'Confidence')}</span>
                  </div>

                  {/* Sets */}
                  {exercise.sets.map((set, setIdx) => (
                    <div key={setIdx} className="grid grid-cols-5 gap-4 mt-3 items-center">
                      <span className={`text-sm font-medium ${
                        set.set_type === 'warmup' ? 'text-yellow-700' : 'text-gray-700'
                      }`}>
                        {set.set_type === 'warmup' ? `W${set.set_number}` : set.set_number}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        set.set_type === 'warmup'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-indigo-100 text-indigo-700'
                      }`}>
                        {set.set_type === 'warmup'
                          ? t('workouts.photo.warmup', 'Warmup')
                          : t('workouts.photo.working', 'Working')
                        }
                      </span>
                      <input
                        type="number"
                        step="0.5"
                        value={editedExercises[exerciseIdx]?.sets[setIdx]?.actual_weight ?? ''}
                        onChange={(e) => handleSetChange(exerciseIdx, setIdx, 'actual_weight', e.target.value)}
                        className="input text-sm py-1"
                        disabled={!isMatched}
                      />
                      <input
                        type="number"
                        value={editedExercises[exerciseIdx]?.sets[setIdx]?.actual_reps ?? ''}
                        onChange={(e) => handleSetChange(exerciseIdx, setIdx, 'actual_reps', e.target.value)}
                        className="input text-sm py-1"
                        disabled={!isMatched}
                      />
                      <span className={`text-xs ${getConfidenceColor(set.confidence)}`}>
                        {Math.round(set.confidence * 100)}%
                      </span>
                    </div>
                  ))}

                  {/* Raw text for unclear values */}
                  {exercise.sets.some(s => s.confidence < 0.6) && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-600 mb-2">
                        {t('workouts.photo.rawTextTitle', 'Raw handwriting interpretation:')}
                      </p>
                      {exercise.sets.filter(s => s.confidence < 0.6).map((s, idx) => (
                        <p key={idx} className="text-xs text-gray-500">
                          {s.set_type === 'warmup' ? `W${s.set_number}` : `${t('workouts.photo.setN', 'Set')} ${s.set_number}`}: &quot;{s.raw_text}&quot;
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Exercise warnings */}
                  {exercise.warnings && exercise.warnings.length > 0 && (
                    <div className="mt-3 text-xs text-yellow-600">
                      {exercise.warnings.map((w, idx) => (
                        <p key={idx}>{w}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Processing Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-gray-600">
          <div>
            <span className="font-medium">{t('workouts.photo.processingTime', 'Processing')}:</span>{' '}
            {(analysis.processing_time_ms / 1000).toFixed(2)}s
          </div>
          <div>
            <span className="font-medium">{t('workouts.photo.apiCost', 'Cost')}:</span> ${analysis.api_cost?.toFixed(4) || 0}
          </div>
          <div>
            <span className="font-medium">{t('workouts.photo.model', 'Model')}:</span> {analysis.model_used}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-4">
        <button
          onClick={handleApply}
          className="flex-1 btn-primary flex items-center justify-center"
          disabled={!analysis.exercises?.some(ex => ex.matched_session_exercise_id)}
        >
          <CheckCircleIcon className="h-5 w-5 mr-2" />
          {t('workouts.photo.applyResults', 'Apply Results')}
        </button>
        <button
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  )
}
